import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, ClockIcon, HashIcon } from '@phosphor-icons/react';
import useSWR from 'swr';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import {
    InfoPanel,
    InfoPanelGroup,
    InfoPanelCard,
    InfoPanelTitle,
    InfoPanelRow,
    InfoPanelValue,
} from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { formatDate } from '@/utils/date';
import type { Person, PersonAnalyticsStats } from '@/types/person';
import { DirectoryItemChart, type PeriodOption } from './DirectoryItemChart';
import { EngineService } from '@/services/engine';
import { getPersonStatsCacheKey } from '@/constants/cache-keys';
import { AppRoutes } from '@/constants/routes';
import { useChartPointNavigation } from '@/hooks/useChartPointNavigation';
import { PERIOD_CONFIG, getStartTime } from '@/utils/chart-periods';
import type { ChartDataPoint } from '@/components/Chart';

interface PersonOverviewTabProps {
    person: Person;
}

export function PersonOverviewTab({ person }: PersonOverviewTabProps) {
    const { t } = useTranslation(I18nNamespace.DIRECTORY);
    const navigate = useNavigate();
    const navigateToChartRange = useChartPointNavigation();
    const [activePeriod, setActivePeriod] = useState<PeriodOption>('7days');

    const config = PERIOD_CONFIG[activePeriod];
    const groupBy = config.groupBy;
    const startTime = getStartTime(config.milliseconds, groupBy);

    const { data: stats, isLoading: statsLoading } =
        useSWR<PersonAnalyticsStats>(
            person.id ? getPersonStatsCacheKey(person.id, activePeriod) : null,
            () =>
                EngineService.getPersonStats(person.id, {
                    startTime,
                    groupBy,
                }),
            { keepPreviousData: true },
        );

    const handleSourceClick = (source: { id: string }) => {
        const params = new URLSearchParams();
        params.set('personId', person.id);
        params.set('sources', source.id);
        navigate(`${AppRoutes.EVENTS}?${params.toString()}`);
    };

    const handlePointClick = (_dataPoint: ChartDataPoint, index: number) => {
        const point = stats?.chartData[index];
        if (!point) return;
        navigateToChartRange(
            AppRoutes.EVENTS,
            { personId: person.id },
            point.startTime,
            point.endTime,
        );
    };

    return (
        <div className="flex flex-col gap-6 pb-24">
            <div className="flex flex-col border-b border-grey-500 @wide:flex-row">
                <InfoPanel>
                    <InfoPanelGroup>
                        <InfoPanelCard>
                            <InfoPanelTitle>
                                {t('personPage.analytics.firstSeen')}
                            </InfoPanelTitle>
                            <InfoPanelRow>
                                <CalendarIcon className="w-4 h-4 text-grey-300 shrink-0" />
                                <InfoPanelValue>
                                    {formatDate(person.createdAt)}
                                </InfoPanelValue>
                            </InfoPanelRow>
                        </InfoPanelCard>
                        <InfoPanelCard>
                            <InfoPanelTitle>
                                {t('personPage.analytics.lastSeen')}
                            </InfoPanelTitle>
                            <InfoPanelRow>
                                <ClockIcon className="w-4 h-4 text-grey-300 shrink-0" />
                                <InfoPanelValue>
                                    {person.lastSeen
                                        ? formatDate(person.lastSeen)
                                        : '—'}
                                </InfoPanelValue>
                            </InfoPanelRow>
                        </InfoPanelCard>
                    </InfoPanelGroup>
                    <InfoPanelGroup>
                        <InfoPanelCard>
                            <InfoPanelTitle>
                                {t('personPage.analytics.detected')}
                            </InfoPanelTitle>
                            <InfoPanelRow>
                                <HashIcon className="w-4 h-4 text-grey-300 shrink-0" />
                                <InfoPanelValue>
                                    {stats?.totalEventsOverall ?? 0}{' '}
                                    {t('personPage.analytics.times')}
                                </InfoPanelValue>
                            </InfoPanelRow>
                        </InfoPanelCard>
                        <InfoPanelCard>
                            <InfoPanelTitle>
                                {t('personPage.analytics.across')}
                            </InfoPanelTitle>
                            <InfoPanelRow>
                                <SecurityCameraIconMirrored className="w-4 h-4 text-grey-300 shrink-0" />
                                <InfoPanelValue>
                                    {stats?.totalSources ?? 0}{' '}
                                    {t('personPage.analytics.sources')}
                                </InfoPanelValue>
                            </InfoPanelRow>
                        </InfoPanelCard>
                    </InfoPanelGroup>
                </InfoPanel>
            </div>

            <DirectoryItemChart
                chartData={stats?.chartData ?? []}
                totalEvents={stats?.totalEvents ?? 0}
                sourceStats={stats?.sourceStats ?? []}
                isLoading={statsLoading}
                activePeriod={activePeriod}
                onPeriodChange={setActivePeriod}
                onPointClick={handlePointClick}
                onSourceClick={handleSourceClick}
            />
        </div>
    );
}
